import * as React from 'react';
import { Text } from '@oneaudi/unified-web-components';
import { v4 as uuid } from 'uuid';
import { renderTextWithFootnotesReferences } from '@oneaudi/feature-app-utils';
import type { VueFormatterServiceInterfaceV1 } from '@oneaudi/vue-formatter-service/lib/cjs/v1/vue-formatter-feature-service';
import styled from 'styled-components';
import {
  SYS_SPACE_FIXED_500,
  SYS_SPACE_FIXED_800,
  SYS_SPACE_FIXED_600,
  SYS_SPACE_FIXED_1000,
  SYS_SPACE_FIXED_1200,
  SYS_SPACE_FIXED_200,
} from '@oneaudi/unified-web-common';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { constants } from './constants';
import type { WltpProps } from './vueServiceHelper';
import { VueServiceHelper } from './vueServiceHelper';

type ConsumptionAndEmissionComponentProps = {
  consuptionAndEmission: string[] | null;
  disclaimer: string;
  theme: string;
  vueFormatterService: VueFormatterServiceInterfaceV1;
  align: 'center' | 'auto' | 'baseline' | 'end' | 'start' | 'stretch';
  localeService: LocaleServiceV1;
};

export const ConsumptionAndEmission: React.FC<ConsumptionAndEmissionComponentProps> = (props) => {
  const { consuptionAndEmission, vueFormatterService, theme, disclaimer, align, localeService } =
    props;

  const [vueTexts, setVueTexts] = React.useState([] as WltpProps[]);

  React.useEffect(() => {
    const fetchConsumptionAndEmission = async () => {
      if (consuptionAndEmission && props?.localeService && vueFormatterService)
        await VueServiceHelper.getConsumptionAndEmission(
          consuptionAndEmission,
          setVueTexts,
          vueFormatterService,
          localeService,
        );
    };

    fetchConsumptionAndEmission().catch(console.error);
  }, [consuptionAndEmission]);

  return (
    <ContentContainer align={align}>
      <ConsumptionAndEmissionContainer>
        {vueTexts &&
          vueTexts?.map(
            (
              {
                formattedConsumption,
                formattedEmission,
                formattedCo2Class,
                formattedDischargedCo2Class,
                formattedDischargedConsumption,
              },
              index,
            ) => (
              <Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }} key={index}>
                <div>
                  {formattedConsumption && (
                    <span className="sm-wltp-items" tabIndex={-1} key={uuid()}>
                      {formattedConsumption}
                    </span>
                  )}
                  {formattedEmission && (
                    <span className="sm-wltp-items" tabIndex={-1} key={uuid()}>
                      {formattedEmission}
                    </span>
                  )}
                  {formattedCo2Class && (
                    <span className="sm-wltp-items" tabIndex={-1} key={uuid()}>
                      {formattedCo2Class}
                    </span>
                  )}
                  {formattedDischargedConsumption && (
                    <span className="sm-wltp-items" tabIndex={-1} key={uuid()}>
                      {formattedDischargedConsumption}
                    </span>
                  )}
                  {formattedDischargedCo2Class && (
                    <span className="sm-wltp-items" tabIndex={-1} key={uuid()}>
                      {formattedDischargedCo2Class}
                    </span>
                  )}
                </div>
              </Text>
            ),
          )}
      </ConsumptionAndEmissionContainer>
      <DisclaimerContainer theme={theme} style={{ display: disclaimer ? 'block' : 'none' }}>
        <br />
        {disclaimer && (
          <Text tag="span" variant={{ type: 'copy', order: '2', style: 'normal' }}>
            {renderTextWithFootnotesReferences(disclaimer)}
          </Text>
        )}
      </DisclaimerContainer>
    </ContentContainer>
  );
};

const device = {
  desktop: `(min-width:${constants.displaySizes.desktop})`,
  largeDesktop: `(min-width:${constants.displaySizes.largeDesktop})`,
  smallDesktop: `(min-width:${constants.displaySizes.smallDesktop})`,
  mobile: `(max-width:${constants.displaySizes.mobile})`,
  tablet: `(min-width:${constants.displaySizes.tablet})`,
};

interface ContentContainerProps {
  align: 'center' | 'auto' | 'baseline' | 'end' | 'start' | 'stretch';
}

export const ContentContainer = styled.div<ContentContainerProps>`
  ${process.env.IS_LEGACY
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (props) => `background-color: ${props?.theme?.sys?.color?.canvas?.default}`
    : ''};
  padding: ${SYS_SPACE_FIXED_500};
  text-align: ${({ align }) => align};

  @media ${device.tablet} {
    padding: ${SYS_SPACE_FIXED_600} 0;
  }
  @media ${device.smallDesktop} {
    padding: ${SYS_SPACE_FIXED_800} 0;
  }
  @media ${device.desktop} {
    padding: ${SYS_SPACE_FIXED_1000} 0;
  }
  @media ${device.largeDesktop} {
    padding: ${SYS_SPACE_FIXED_1200} 0;
  }
`;

export const DisclaimerContainer = styled.div`
  ${process.env.IS_LEGACY
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (props) => `background-color: ${props?.theme?.sys?.color?.canvas?.default}`
    : ''};
  position: relative;
  bottom: 0;
  height: fit-content;
  a:focus {
    outline: 5px auto Highlight !important;
    outline: 5px auto -webkit-focus-ring-color !important;
  }
  span sup a {
    font-size: 10px !important;
    font-weight: 500;
    font-feature-settings: normal !important;
  }
  p {
  }
  @media ${device.tablet} {
    display: flex;
    flex-direction: row;
    span {
      margin-right: ${SYS_SPACE_FIXED_200};
    }
  }
`;

export const ConsumptionAndEmissionContainer = styled.div`
  ${process.env.IS_LEGACY
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (props) => `background-color: ${props?.theme?.sys?.color?.canvas?.default}`
    : ''};
  position: relative;
  bottom: 0;
  height: fit-content;
  a:focus {
    outline: 5px auto Highlight !important;
    outline: 5px auto -webkit-focus-ring-color !important;
  }
  span sup a {
    font-size: 10px !important;
    font-weight: 500;
    font-feature-settings: normal;
  }

  @media ${device.tablet} {
    flex-direction: row;
    span {
      margin-right: ${SYS_SPACE_FIXED_200};
    }
    span sup a {
      font-size: 10px !important;
      font-weight: 500;
      font-feature-settings: normal;
    }
  }

  @media screen and (max-width: ${constants.displaySizes.smallDesktop}) {
    span {
      :not(:last-child)::after {
        content: '; ';
      }
    }
  }
`;
